/* eslint-disable max-lines */
import { Injectable } from '@angular/core';

import { HelpTranslationLinkMapKey } from '@recruitee/help-articles';
import { BillingPlanTier } from '@recruitee/http/types';
import { I18nService } from '@recruitee/i18n';

import { GuardedFeature } from './feature-guard.types';
import { PricingAnalyticsFeature, PricingAnalyticsService } from './pricing-analytics.service';

export enum MediaSourceType {
  Image = 'image',
  Video = 'video',
  YoutubeVideo = 'youtube_video',
}

export type FeatureData = {
  title: string;
  description: string;
  plan: BillingPlanTier;
  upgradeUrl: string;
  helpUrl?: string;
  helpArticleKey?: HelpTranslationLinkMapKey;
  learnMoreTranslationKey?: string;
  learnMoreUrlByLang?: {
    en: string;
    de: string;
    fr: string;
    nl: string;
  };
  mediaSource: string;
  mediaSourceType: MediaSourceType;
  fallbackImgSource?: string;
  analytics?: {
    discrimination: () => void;
    cta: () => void;
  };
  noContentWidth?: boolean;
};

@Injectable({ providedIn: 'root' })
export class FeaturesGuardModel {
  public readonly data: { [key: string]: FeatureData } = {
    [GuardedFeature.SendLater]: {
      mediaSource: 'assets/images/feature-guard/send-later.svg',
      title: this.i18n.translate('app.mailbox.send_later'),
      description: this.i18n.translate('app.pricing.feature_guard.send_later_desc'),
      helpArticleKey: 'scheduleEmailsToBeSentLater',
      plan: BillingPlanTier.Scale,
      upgradeUrl: '/mailbox/upgrade',
      mediaSourceType: MediaSourceType.Image,
      analytics: {
        discrimination: () =>
          this.pricingAnalyticsService.trackFeatureDiscriminated(PricingAnalyticsFeature.SendLater),
        cta: () =>
          this.pricingAnalyticsService.trackUpgradeCTAClick(PricingAnalyticsFeature.SendLater),
      },
    },
    [GuardedFeature.Scheduler]: {
      mediaSource: 'assets/images/feature-guard/scheduler.svg',
      title: this.i18n.translate('app.pricing.plan_picker.features.scheduler'),
      description: this.i18n.translate('app.pricing.feature_guard.scheduler_desc'),
      helpArticleKey: 'scheduleCandidateInterview',
      plan: BillingPlanTier.Scale,
      upgradeUrl: '/settings/upgrade/scheduler',
      mediaSourceType: MediaSourceType.Image,
      analytics: {
        discrimination: () =>
          this.pricingAnalyticsService.trackFeatureDiscriminated(PricingAnalyticsFeature.Scheduler),
        cta: () =>
          this.pricingAnalyticsService.trackUpgradeCTAClick(PricingAnalyticsFeature.Scheduler),
      },
    },
    [GuardedFeature.DefaultProfileFields]: {
      mediaSource: 'assets/images/feature-guard/profile-fields.svg',
      title: this.i18n.translate('app.pricing.feature_guard.profile_fields'),
      description: this.i18n.translate('app.pricing.feature_guard.profile_fields_desc'),
      helpArticleKey: 'manageProfileFields',
      plan: BillingPlanTier.Scale,
      upgradeUrl: '/settings/upgrade/profile_fields',
      mediaSourceType: MediaSourceType.Image,
      analytics: {
        discrimination: () =>
          this.pricingAnalyticsService.trackFeatureDiscriminated(
            PricingAnalyticsFeature.ProfileFieldsTemplates,
          ),
        cta: () =>
          this.pricingAnalyticsService.trackUpgradeCTAClick(
            PricingAnalyticsFeature.ProfileFieldsTemplates,
          ),
      },
    },
    [GuardedFeature.AutomatedActions]: {
      title: this.i18n.translate('app.automated_actions.automated_actions'),
      description: this.i18n.translate('app.pricing.feature_guard.automated_actions_desc'),
      helpArticleKey: 'automatedActions',
      plan: BillingPlanTier.Scale,
      upgradeUrl: '',
      mediaSource: 'assets/images/feature-guard/automated-actions.svg',
      mediaSourceType: MediaSourceType.Image,
      analytics: {
        discrimination: () =>
          this.pricingAnalyticsService.trackFeatureDiscriminated(
            PricingAnalyticsFeature.AutomatedActions,
          ),
        cta: () =>
          this.pricingAnalyticsService.trackUpgradeCTAClick(
            PricingAnalyticsFeature.AutomatedActions,
          ),
      },
    },
    [GuardedFeature.Pipelines]: {
      mediaSource: 'assets/video/feature-upgrade/workflow_templates_pipeline.mp4',
      title: this.i18n.translate('app.pricing.feature_guard.pipelines.title'),
      description: this.i18n.translate('app.pricing.feature_guard.pipelines.description'),
      helpArticleKey: 'pipelineTemplatesBeta',
      plan: BillingPlanTier.Scale,
      upgradeUrl: '',
      mediaSourceType: MediaSourceType.Video,
      analytics: {
        discrimination: () =>
          this.pricingAnalyticsService.trackFeatureDiscriminated(
            PricingAnalyticsFeature.PipelineTemplates,
          ),
        cta: () =>
          this.pricingAnalyticsService.trackUpgradeCTAClick(
            PricingAnalyticsFeature.PipelineTemplates,
          ),
      },
    },
    [GuardedFeature.Multilang]: {
      mediaSource: 'assets/images/feature-guard/multilang.svg',
      title: this.i18n.translate('app.pricing.feature_guard.multilang'),
      description: this.i18n.translate('app.pricing.feature_guard.multilang_desc'),
      helpArticleKey: 'multilang',
      plan: BillingPlanTier.Scale,
      upgradeUrl: '/settings/upgrade/multilang',
      mediaSourceType: MediaSourceType.Image,
      analytics: {
        discrimination: () =>
          this.pricingAnalyticsService.trackFeatureDiscriminated(PricingAnalyticsFeature.Multilang),
        cta: () =>
          this.pricingAnalyticsService.trackUpgradeCTAClick(PricingAnalyticsFeature.Multilang),
      },
    },
    [GuardedFeature.EvaluationForms]: {
      mediaSource: 'assets/images/feature-guard/evaluation-form.svg',
      title: this.i18n.translate('app.pricing.feature_guard.evaluation_forms.title'),
      description: this.i18n.translate('app.pricing.feature_guard.evaluation_forms.description'),
      helpArticleKey: 'detailedCandidateEvaluations',
      plan: BillingPlanTier.Scale,
      upgradeUrl: '/settings/upgrade/evaluation_forms',
      mediaSourceType: MediaSourceType.Image,
      analytics: {
        discrimination: () =>
          this.pricingAnalyticsService.trackFeatureDiscriminated(
            PricingAnalyticsFeature.EvaluationForms,
          ),
        cta: () =>
          this.pricingAnalyticsService.trackUpgradeCTAClick(
            PricingAnalyticsFeature.EvaluationForms,
          ),
      },
    },
    [GuardedFeature.Questionnaires]: {
      mediaSource: 'assets/images/feature-guard/questionnaires.svg',
      title: this.i18n.translate('app.pricing.feature_guard.questionnaires.title'),
      description: this.i18n.translate('app.pricing.feature_guard.questionnaires.description'),
      helpArticleKey: 'sendQuestionnaires',
      plan: BillingPlanTier.Scale,
      upgradeUrl: '/settings/upgrade/questionnaires',
      mediaSourceType: MediaSourceType.Image,
      analytics: {
        discrimination: () =>
          this.pricingAnalyticsService.trackFeatureDiscriminated(
            PricingAnalyticsFeature.Questionnaires,
          ),
        cta: () =>
          this.pricingAnalyticsService.trackUpgradeCTAClick(PricingAnalyticsFeature.Questionnaires),
      },
    },
    [GuardedFeature.CustomDomain]: {
      mediaSource: 'assets/images/feature-guard/custom_domain.png',
      title: this.i18n.translate('app.pricing.feature_guard.custom_domain.title'),
      description: this.i18n.translate('app.pricing.feature_guard.custom_domain.description'),
      helpArticleKey: 'customDomain',
      plan: BillingPlanTier.Scale,
      upgradeUrl: '',
      mediaSourceType: MediaSourceType.Image,
      analytics: {
        discrimination: () =>
          this.pricingAnalyticsService.trackFeatureDiscriminated(
            PricingAnalyticsFeature.CustomDomain,
          ),
        cta: () =>
          this.pricingAnalyticsService.trackUpgradeCTAClick(PricingAnalyticsFeature.CustomDomain),
      },
    },
    [GuardedFeature.AdvancedReports]: {
      mediaSource: 'assets/video/feature-upgrade/reports.mp4',
      title: this.i18n.translate('app.pricing.feature_guard.advanced_reporting'),
      description: this.i18n.translate('app.pricing.feature_guard.advanced_reporting_desc'),
      learnMoreUrlByLang: {
        en: 'https://blog.recruitee.com/recruitee/product-updates/recruitment-metrics/',
        de: 'https://recruitee.com/predict/recruitment-analytics',
        fr: 'https://recruitee.com/predict/recruitment-analytics',
        nl: 'https://recruitee.com/predict/recruitment-analytics',
      },
      plan: BillingPlanTier.Scale,
      upgradeUrl: '/reports/upgrade',
      mediaSourceType: MediaSourceType.Video,
      analytics: {
        discrimination: () =>
          this.pricingAnalyticsService.trackFeatureDiscriminated(
            PricingAnalyticsFeature.AdvancedReports,
          ),
        cta: () =>
          this.pricingAnalyticsService.trackUpgradeCTAClick(
            PricingAnalyticsFeature.AdvancedReports,
          ),
      },
    },
    [GuardedFeature.ReportsBuilderLaunch]: {
      mediaSource: 'https://www.youtube.com/embed/-t0VuhB2gfI',
      title: this.i18n.translate('app.pricing.feature_guard.reports_builder_launch'),
      description: this.i18n.translate('app.pricing.feature_guard.reports_builder_launch_desc'),
      helpArticleKey: 'reportsBuilder',
      plan: BillingPlanTier.Launch,
      upgradeUrl: '/reports/upgrade-reports-builder',
      mediaSourceType: MediaSourceType.YoutubeVideo,
      analytics: {
        discrimination: () =>
          this.pricingAnalyticsService.trackFeatureDiscriminated(
            PricingAnalyticsFeature.ReportsBuilderLaunch,
          ),
        cta: () =>
          this.pricingAnalyticsService.trackUpgradeCTAClick(
            PricingAnalyticsFeature.ReportsBuilderLaunch,
          ),
      },
    },
    [GuardedFeature.ReportsBuilderScale]: {
      mediaSource: 'assets/images/empty-states/reports-empty-dashboard.svg',
      title: this.i18n.translate('app.pricing.feature_guard.reports_builder_scale'),
      description: this.i18n.translate('app.pricing.feature_guard.reports_builder_scale_desc'),
      helpArticleKey: 'reportsBuilder',
      plan: BillingPlanTier.Scale,
      upgradeUrl: '/reports-new/upgrade',
      mediaSourceType: MediaSourceType.Image,
      analytics: {
        discrimination: () =>
          this.pricingAnalyticsService.trackFeatureDiscriminated(PricingAnalyticsFeature.Reports),
        cta: () =>
          this.pricingAnalyticsService.trackUpgradeCTAClick(PricingAnalyticsFeature.Reports),
      },
    },
    [GuardedFeature.ReportsBuilderScaleVideo]: {
      mediaSource: 'https://www.youtube.com/embed/-t0VuhB2gfI',
      title: this.i18n.translate('app.pricing.feature_guard.reports_builder_scale'),
      description: this.i18n.translate('app.pricing.feature_guard.reports_builder_scale_desc'),
      helpArticleKey: 'reportsBuilder',
      plan: BillingPlanTier.Scale,
      upgradeUrl: '/reports-new/upgrade',
      mediaSourceType: MediaSourceType.YoutubeVideo,
      analytics: {
        discrimination: () =>
          this.pricingAnalyticsService.trackFeatureDiscriminated(PricingAnalyticsFeature.Reports),
        cta: () =>
          this.pricingAnalyticsService.trackUpgradeCTAClick(PricingAnalyticsFeature.Reports),
      },
    },
    [GuardedFeature.SSO]: {
      mediaSource: 'assets/video/feature-upgrade/sso.mp4',
      title: this.i18n.translate('app.pricing.feature_guard.sso.title'),
      description: this.i18n.translate('app.pricing.feature_guard.sso.description'),
      helpArticleKey: 'ssoOverview',
      learnMoreTranslationKey: 'app.pricing.feature_guard.sso.cta',
      plan: BillingPlanTier.Lead,
      upgradeUrl: '/settings/company/upgrade_sso',
      mediaSourceType: MediaSourceType.Video,
      analytics: {
        discrimination: () =>
          this.pricingAnalyticsService.trackFeatureDiscriminated(PricingAnalyticsFeature.SSO),
        cta: () => this.pricingAnalyticsService.trackUpgradeCTAClick(PricingAnalyticsFeature.SSO),
      },
    },
    [GuardedFeature.Visibility]: {
      mediaSource: 'assets/video/feature-upgrade/visibility.mp4',
      title: this.i18n.translate('app.pricing.feature_guard.visibility.title'),
      description: this.i18n.translate('app.pricing.feature_guard.visibility.description'),
      helpArticleKey: 'visibility',
      plan: BillingPlanTier.Scale,
      upgradeUrl: '',
      mediaSourceType: MediaSourceType.Video,
      analytics: {
        discrimination: () =>
          this.pricingAnalyticsService.trackFeatureDiscriminated(
            PricingAnalyticsFeature.Visibility,
          ),
        cta: () =>
          this.pricingAnalyticsService.trackUpgradeCTAClick(PricingAnalyticsFeature.Visibility),
      },
      // eslint-disable-next-line max-lines
    },
    [GuardedFeature.AdjustableRoles]: {
      mediaSource: 'assets/images/feature-guard/roles.svg',
      title: this.i18n.translate('app.pricing.feature_guard.adjustable_roles.title'),
      description: this.i18n.translate('app.pricing.feature_guard.adjustable_roles.description'),
      helpArticleKey: 'roles',
      plan: BillingPlanTier.Scale,
      upgradeUrl: '',
      mediaSourceType: MediaSourceType.Image,
      analytics: {
        discrimination: () =>
          this.pricingAnalyticsService.trackFeatureDiscriminated(PricingAnalyticsFeature.Roles),
        cta: () => this.pricingAnalyticsService.trackUpgradeCTAClick(PricingAnalyticsFeature.Roles),
      },
    },
    [GuardedFeature.OverdueCandidates]: {
      title: this.i18n.translate('app.overdue_candidates.overdue_candidate_reminders'),
      description: this.i18n.translate('app.overdue_candidates.feature_guard_desc'),
      helpArticleKey: 'overdueCandidates',
      // eslint-disable-next-line max-lines
      plan: BillingPlanTier.Launch,
      upgradeUrl: '',
      mediaSource: 'assets/video/feature-upgrade/overdue_candidates.mp4',
      mediaSourceType: MediaSourceType.Video,
      fallbackImgSource: 'assets/images/feature-guard/overdue-candidates.svg',
      analytics: {
        discrimination: () =>
          this.pricingAnalyticsService.trackFeatureDiscriminated(
            PricingAnalyticsFeature.OverdueCandidates,
          ),
        cta: () =>
          this.pricingAnalyticsService.trackUpgradeCTAClick(
            PricingAnalyticsFeature.OverdueCandidates,
          ),
      },
    },
    [GuardedFeature.OpenQuestions]: {
      mediaSource: 'assets/video/feature-upgrade/workflow_templates_open_questions.mp4',
      title: this.i18n.translate('app.pricing.feature_guard.screening_questions.title'),
      description: this.i18n.translate('app.pricing.feature_guard.screening_questions.description'),
      helpArticleKey: 'screeningQuestionsTemplates',
      plan: BillingPlanTier.Scale,
      upgradeUrl: '/settings/upgrade/screening_questions',
      mediaSourceType: MediaSourceType.Video,
      analytics: {
        discrimination: () =>
          this.pricingAnalyticsService.trackFeatureDiscriminated(
            PricingAnalyticsFeature.ScreeningQuestions,
          ),
        cta: () =>
          this.pricingAnalyticsService.trackUpgradeCTAClick(
            PricingAnalyticsFeature.ScreeningQuestions,
          ),
      },
    },
    [GuardedFeature.GDPRAutomations]: {
      mediaSource: 'assets/video/feature-upgrade/gdpr_automation.mp4',
      title: this.i18n.translate('app.pricing.feature_guard.gdpr_automations.title'),
      description: this.i18n.translate('app.pricing.feature_guard.gdpr_automations.description'),
      helpArticleKey: 'gdprSettingsAutomationsConsentDeleteRequest',
      plan: BillingPlanTier.Scale,
      upgradeUrl: '',
      mediaSourceType: MediaSourceType.Video,
      analytics: {
        discrimination: () =>
          this.pricingAnalyticsService.trackFeatureDiscriminated(
            PricingAnalyticsFeature.GDPRAutomations,
          ),
        cta: () =>
          this.pricingAnalyticsService.trackUpgradeCTAClick(
            PricingAnalyticsFeature.GDPRAutomations,
          ),
      },
    },
    [GuardedFeature.Webhooks]: {
      mediaSource: 'assets/images/feature-guard/webhooks.svg',
      title: this.i18n.translate('app.webhooks.feature_guard.title'),
      description: this.i18n.translate('app.webhooks.feature_guard.description'),
      // TODO: Add correct URL when we have somewhere to point at
      helpUrl: 'https://docs.recruitee.com/reference',
      plan: BillingPlanTier.Scale,
      upgradeUrl: '/settings/upgrade/webhooks',
      mediaSourceType: MediaSourceType.Image,
      analytics: {
        discrimination: () =>
          this.pricingAnalyticsService.trackFeatureDiscriminated(PricingAnalyticsFeature.Webhooks),
        cta: () =>
          this.pricingAnalyticsService.trackUpgradeCTAClick(PricingAnalyticsFeature.Webhooks),
      },
    },
    [GuardedFeature.MeetingRooms]: {
      mediaSource: 'assets/video/feature-upgrade/meeting_rooms.mp4',
      title: this.i18n.translate('app.settings.meeting_rooms.heading'),
      description: this.i18n.translate('app.settings.meeting_rooms.subheading'),
      helpArticleKey: 'meetingRoms',
      plan: BillingPlanTier.Scale,
      upgradeUrl: '/settings/upgrade/meeting_rooms',
      mediaSourceType: MediaSourceType.Video,
      analytics: {
        discrimination: () =>
          this.pricingAnalyticsService.trackFeatureDiscriminated(
            PricingAnalyticsFeature.MeetingRooms,
          ),
        cta: () =>
          this.pricingAnalyticsService.trackUpgradeCTAClick(PricingAnalyticsFeature.MeetingRooms),
      },
    },
    [GuardedFeature.JobScheduler]: {
      mediaSource: 'assets/images/feature-guard/job_scheduler.svg',
      title: this.i18n.translate('app.pricing.feature_guard.job_scheduler.title'),
      description: this.i18n.translate('app.pricing.feature_guard.job_scheduler.description'),
      helpUrl: 'jobScheduler',
      plan: BillingPlanTier.Scale,
      upgradeUrl: '',
      mediaSourceType: MediaSourceType.Image,
      analytics: {
        discrimination: () =>
          this.pricingAnalyticsService.trackFeatureDiscriminated(
            PricingAnalyticsFeature.JobScheduler,
          ),
        cta: () =>
          this.pricingAnalyticsService.trackUpgradeCTAClick(PricingAnalyticsFeature.JobScheduler),
      },
    },
    [GuardedFeature.ProfilePrint]: {
      mediaSource: 'assets/images/feature-guard/profile-print.svg',
      title: this.i18n.translate('app.pricing.feature_guard.profile_print.title'),
      description: this.i18n.translate('app.pricing.feature_guard.profile_print.description'),
      helpArticleKey: 'profilePrint',
      plan: BillingPlanTier.Scale,
      upgradeUrl: '',
      mediaSourceType: MediaSourceType.Image,
      analytics: {
        discrimination: () =>
          this.pricingAnalyticsService.trackFeatureDiscriminated(
            PricingAnalyticsFeature.ProfilePrint,
          ),
        cta: () =>
          this.pricingAnalyticsService.trackUpgradeCTAClick(PricingAnalyticsFeature.ProfilePrint),
      },
    },
    [GuardedFeature.Security]: {
      mediaSource: 'assets/images/feature-guard/security.svg',
      title: this.i18n.translate('app.pricing.feature_guard.security.title'),
      description: this.i18n.translate('app.pricing.feature_guard.security.description'),
      helpArticleKey: 'security',
      plan: BillingPlanTier.Launch,
      upgradeUrl: '',
      mediaSourceType: MediaSourceType.Image,
    },
    [GuardedFeature.EEO]: {
      mediaSource: 'assets/images/feature-guard/eeo.svg',
      title: this.i18n.translate('app.pricing.feature_guard.eeo.title'),
      description: this.i18n.translate('app.pricing.feature_guard.eeo.description'),
      helpArticleKey: 'eeoIntro',
      plan: BillingPlanTier.Scale,
      upgradeUrl: '',
      mediaSourceType: MediaSourceType.Image,
      analytics: {
        discrimination: () =>
          this.pricingAnalyticsService.trackFeatureDiscriminated(PricingAnalyticsFeature.EEO),
        cta: () => this.pricingAnalyticsService.trackUpgradeCTAClick(PricingAnalyticsFeature.EEO),
      },
    },
    [GuardedFeature.Referrals]: {
      mediaSource: 'https://www.youtube.com/embed/ISmE0d8mz38',
      title: this.i18n.translate('app.settings.referrals.promotional.title'),
      description: this.i18n.translate('app.settings.referrals.promotional.description'),
      helpArticleKey: 'referralsPromotional',
      plan: BillingPlanTier.Lead,
      upgradeUrl: '/settings/referrals/promotional',
      mediaSourceType: MediaSourceType.YoutubeVideo,
    },
    [GuardedFeature.AuditLogs]: {
      mediaSource: 'assets/images/feature-guard/audit-logs.png',
      title: this.i18n.translate('app.pricing.feature_guard.audit_logs.title'),
      description: this.i18n.translate('app.pricing.feature_guard.audit_logs.description'),
      helpArticleKey: 'auditLogs',
      plan: BillingPlanTier.Launch,
      upgradeUrl: '/settings/upgrade/audit_logs',
      mediaSourceType: MediaSourceType.Image,
      analytics: {
        discrimination: () =>
          this.pricingAnalyticsService.trackFeatureDiscriminated(PricingAnalyticsFeature.AuditLogs),
        cta: () =>
          this.pricingAnalyticsService.trackUpgradeCTAClick(PricingAnalyticsFeature.AuditLogs),
      },
    },
    [GuardedFeature.FairEvaluations]: {
      mediaSource: 'https://www.youtube.com/embed/gnfR1cvlegA',
      title: this.i18n.translate('app.pricing.feature_guard.fair_evaluations.title'),
      description: this.i18n.translate('app.pricing.feature_guard.fair_evaluations.description'),
      helpArticleKey: 'fairEvaluations',
      plan: BillingPlanTier.Scale,
      mediaSourceType: MediaSourceType.YoutubeVideo,
      upgradeUrl: '',
      noContentWidth: true,
      analytics: {
        discrimination: () =>
          this.pricingAnalyticsService.trackFeatureDiscriminated(
            PricingAnalyticsFeature.FairEvaluations,
          ),
        cta: () =>
          this.pricingAnalyticsService.trackUpgradeCTAClick(
            PricingAnalyticsFeature.FairEvaluations,
          ),
      },
    },
    [GuardedFeature.NotificationsPerRole]: {
      mediaSource: 'assets/images/feature-guard/notifications-per-role.png',
      title: this.i18n.translate('app.pricing.feature_guard.notifications_per_role.title'),
      description: this.i18n.translate(
        'app.pricing.feature_guard.notifications_per_role.description',
      ),
      helpArticleKey: 'notificationsPerRole',
      plan: BillingPlanTier.Scale,
      upgradeUrl: '',
      mediaSourceType: MediaSourceType.Image,
      analytics: {
        discrimination: () =>
          this.pricingAnalyticsService.trackFeatureDiscriminated(
            PricingAnalyticsFeature.NotificationsPerRole,
          ),
        cta: () =>
          this.pricingAnalyticsService.trackUpgradeCTAClick(
            PricingAnalyticsFeature.NotificationsPerRole,
          ),
      },
    },
    [GuardedFeature.KnockoutQuestions]: {
      mediaSource: 'assets/images/feature-guard/knockout-questions.png',
      title: this.i18n.translate('app.pricing.feature_guard.knockout_questions.title'),
      description: this.i18n.translate('app.pricing.feature_guard.knockout_questions.description'),
      helpArticleKey: 'knockoutQuestions',
      plan: BillingPlanTier.Scale,
      upgradeUrl: '',
      mediaSourceType: MediaSourceType.Image,
      analytics: {
        discrimination: () =>
          this.pricingAnalyticsService.trackFeatureDiscriminated(
            PricingAnalyticsFeature.KnockoutQuestions,
          ),
        cta: () =>
          this.pricingAnalyticsService.trackUpgradeCTAClick(
            PricingAnalyticsFeature.KnockoutQuestions,
          ),
      },
    },
    [GuardedFeature.Requisitions]: {
      mediaSource: 'assets/images/feature-guard/requisitions.svg',
      title: this.i18n.translate('app.pricing.feature_guard.requisitions.title'),
      description: this.i18n.translate('app.pricing.feature_guard.requisitions.description'),
      helpArticleKey: 'introductionToRequisitionApprovals',
      plan: BillingPlanTier.Lead,
      upgradeUrl: '/settings/upgrade/requisition_approvals',
      mediaSourceType: MediaSourceType.Image,
      analytics: {
        discrimination: () =>
          this.pricingAnalyticsService.trackFeatureDiscriminated(
            PricingAnalyticsFeature.Requisitions,
          ),
        cta: () =>
          this.pricingAnalyticsService.trackUpgradeCTAClick(PricingAnalyticsFeature.Requisitions),
      },
      noContentWidth: true,
    },
  };

  // Lead has everything from Scale and Launch, Scale has everything from Launch, Legacy is a special case where features need to be listed explicitly
  public readonly planFeatures: { [key: string]: GuardedFeature[] } = {
    [BillingPlanTier.Launch]: [
      GuardedFeature.OverdueCandidates,
      GuardedFeature.ReportsBuilderLaunch,
      GuardedFeature.Security,
      GuardedFeature.AuditLogs,
    ],
    [BillingPlanTier.Scale]: [
      GuardedFeature.AdjustableRoles,
      GuardedFeature.AdvancedReports,
      GuardedFeature.ReportsBuilderScale,
      GuardedFeature.AutomatedActions,
      GuardedFeature.CustomDomain,
      GuardedFeature.DefaultProfileFields,
      GuardedFeature.EEO,
      GuardedFeature.EvaluationForms,
      GuardedFeature.Multilang,
      GuardedFeature.Pipelines,
      GuardedFeature.Questionnaires,
      GuardedFeature.Scheduler,
      GuardedFeature.OpenQuestions,
      GuardedFeature.SendLater,
      GuardedFeature.SourcingExtension,
      GuardedFeature.Visibility,
      GuardedFeature.EventInvitationEmails,
      GuardedFeature.Webhooks,
      GuardedFeature.MeetingRooms,
      GuardedFeature.JobScheduler,
      GuardedFeature.ProfilePrint,
      GuardedFeature.FairEvaluations,
      GuardedFeature.NotificationsPerRole,
      GuardedFeature.KnockoutQuestions,
    ],
    [BillingPlanTier.Lead]: [
      GuardedFeature.Referrals,
      GuardedFeature.SSO,
      GuardedFeature.Requisitions,
    ],
    [BillingPlanTier.Start]: [
      GuardedFeature.OverdueCandidates,
      GuardedFeature.ReportsBuilderLaunch,
      GuardedFeature.Security,
      GuardedFeature.AuditLogs,
      GuardedFeature.AutomatedActions,
      GuardedFeature.AdjustableRoles,
    ],
    [BillingPlanTier.Grow]: [
      GuardedFeature.AdvancedReports,
      GuardedFeature.ReportsBuilderScale,
      GuardedFeature.CustomDomain,
      GuardedFeature.DefaultProfileFields,
      GuardedFeature.EEO,
      GuardedFeature.EvaluationForms,
      GuardedFeature.Multilang,
      GuardedFeature.Pipelines,
      GuardedFeature.Questionnaires,
      GuardedFeature.Scheduler,
      GuardedFeature.OpenQuestions,
      GuardedFeature.SendLater,
      GuardedFeature.SourcingExtension,
      GuardedFeature.Visibility,
      GuardedFeature.EventInvitationEmails,
      GuardedFeature.Webhooks,
      GuardedFeature.MeetingRooms,
      GuardedFeature.JobScheduler,
      GuardedFeature.ProfilePrint,
      GuardedFeature.NotificationsPerRole,
      GuardedFeature.KnockoutQuestions,
      GuardedFeature.Referrals,
    ],
    [BillingPlanTier.Optimize]: [
      GuardedFeature.FairEvaluations,
      GuardedFeature.SSO,
      GuardedFeature.Requisitions,
    ],
  };

  constructor(
    private i18n: I18nService,
    private pricingAnalyticsService: PricingAnalyticsService,
  ) {}
}
